/**
 * Timesheet Settings Service
 * Manages organisation-level timesheet configuration with localStorage persistence
 */

import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import { prefixKey } from '@/utils/storagePrefix';
import type { 
  OrganisationTimesheetSettings,
  TimesheetDeadlineSettings,
  UtilisationSettings,
  WorkingProfile,
  PolicyViolationSettings,
  PolicyLimits,
  ApprovalScreenSettings,
} from '@/types/timesheetSettings';

const STORAGE_KEY = prefixKey('timetrack_org_settings');

/**
 * Get organisation timesheet settings
 * Returns default settings if none are saved
 */
export function getOrganisationSettings(): OrganisationTimesheetSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all fields exist
      return {
        ...DEFAULT_TIMESHEET_SETTINGS,
        ...parsed,
        deadline: {
          ...DEFAULT_TIMESHEET_SETTINGS.deadline,
          ...parsed.deadline
        },
        workingProfile: {
          ...DEFAULT_TIMESHEET_SETTINGS.workingProfile,
          ...parsed.workingProfile,
          workingDays: {
            ...DEFAULT_TIMESHEET_SETTINGS.workingProfile.workingDays,
            ...parsed.workingProfile?.workingDays
          }
        },
        policyViolations: {
          ...DEFAULT_TIMESHEET_SETTINGS.policyViolations,
          ...parsed.policyViolations
        },
        policyLimits: parsed.policyLimits ? {
          ...DEFAULT_TIMESHEET_SETTINGS.policyLimits,
          ...parsed.policyLimits
        } : undefined,
        hoursLimits: {
          ...DEFAULT_TIMESHEET_SETTINGS.hoursLimits,
          ...parsed.hoursLimits
        },
        utilisation: {
          ...DEFAULT_TIMESHEET_SETTINGS.utilisation,
          ...parsed.utilisation
        }
      };
    }
  } catch (error) {
    console.error('Error loading organisation settings:', error);
  }
  
  return DEFAULT_TIMESHEET_SETTINGS;
}

/**
 * Save organisation timesheet settings
 */
export function saveOrganisationSettings(settings: OrganisationTimesheetSettings): void {
  try {
    const toSave = {
      ...settings,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving organisation settings:', error);
    throw new Error('Failed to save settings');
  }
}

/**
 * Update deadline settings only
 */
export function updateDeadlineSettings(deadline: Partial<TimesheetDeadlineSettings>): OrganisationTimesheetSettings {
  const current = getOrganisationSettings();
  const updated = {
    ...current,
    deadline: {
      ...current.deadline,
      ...deadline
    }
  };
  saveOrganisationSettings(updated);
  return updated;
}

/**
 * Update working hours settings (working profile, policy violations, policy limits, and approval screen)
 */
export function updateWorkingHoursSettings(
  workingProfile: WorkingProfile,
  policyViolations: PolicyViolationSettings,
  policyLimits?: PolicyLimits,
  approvalScreen?: ApprovalScreenSettings
): OrganisationTimesheetSettings {
  const current = getOrganisationSettings();
  const updated = {
    ...current,
    workingProfile,
    policyViolations,
    policyLimits,
    ...(approvalScreen !== undefined ? { approvalScreen } : {}),
  };
  saveOrganisationSettings(updated);
  return updated;
}

/**
 * Update utilisation settings only
 */
export function updateUtilisationSettings(utilisation: Partial<UtilisationSettings>): OrganisationTimesheetSettings {
  const current = getOrganisationSettings();
  const updated = {
    ...current,
    utilisation: {
      ...current.utilisation,
      ...utilisation
    }
  };
  saveOrganisationSettings(updated);
  return updated;
}

/**
 * Reset to default settings
 */
export function resetToDefaults(): OrganisationTimesheetSettings {
  saveOrganisationSettings(DEFAULT_TIMESHEET_SETTINGS);
  return DEFAULT_TIMESHEET_SETTINGS;
}

/**
 * Get the deadline date for a given week
 *
 * When the configured deadline day is the same day-of-week as the week start,
 * the deadline falls at the end of that week (i.e. the following week's
 * occurrence of that day), never at the start of the week being worked on.
 *
 * @param weekStart - Start date of the week (Monday)
 * @param overrideSettings - Optional settings to use instead of reading from localStorage
 * @returns The deadline date for that week
 */
export function getDeadlineForWeek(weekStart: Date, overrideSettings?: OrganisationTimesheetSettings): Date {
  const settings = overrideSettings ?? getOrganisationSettings();
  const deadline = new Date(weekStart);
  
  // Calculate offset from weekStart's day-of-week to the configured deadline day
  const baseDayOfWeek = deadline.getDay(); // 0=Sunday, 1=Monday, etc.
  const offset = (settings.deadline.deadlineDay - baseDayOfWeek + 7) % 7;
  const daysToAdd = offset === 0 ? 7 : offset;
  deadline.setDate(deadline.getDate() + daysToAdd);
  
  // Set the time
  const [hours, minutes] = settings.deadline.deadlineTime.split(':').map(Number);
  deadline.setHours(hours, minutes, 0, 0);
  
  return deadline;
}

/**
 * Check if a submission date is considered "on time"
 * @param submittedDate - When the timesheet was submitted
 * @param weekStart - Start date of the week
 * @param overrideSettings - Optional settings to use instead of reading from localStorage
 * @returns true if on time (within grace period), false if late
 */
export function isSubmissionOnTime(submittedDate: Date, weekStart: Date, overrideSettings?: OrganisationTimesheetSettings): boolean {
  const settings = overrideSettings ?? getOrganisationSettings();
  const deadline = getDeadlineForWeek(weekStart, settings);
  
  // Add grace period to deadline
  const graceDeadline = new Date(deadline);
  graceDeadline.setDate(graceDeadline.getDate() + settings.deadline.gracePeriodDays);
  
  return submittedDate <= graceDeadline;
}

/**
 * Calculate days late for a submission
 * @param submittedDate - When the timesheet was submitted
 * @param weekStart - Start date of the week
 * @returns Number of days late (0 if on time, negative if early)
 */
export function getDaysLate(submittedDate: Date, weekStart: Date): number {
  const deadline = getDeadlineForWeek(weekStart);
  const diffMs = submittedDate.getTime() - deadline.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Get days until deadline for a given week
 * @param weekStart - Start date of the week
 * @param referenceDate - Date to calculate from (default: today)
 * @returns Number of days until deadline (negative if past deadline)
 */
export function getDaysUntilDeadline(weekStart: Date, referenceDate: Date = new Date()): number {
  const deadline = getDeadlineForWeek(weekStart);
  const diffMs = deadline.getTime() - referenceDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Check if hours exceed daily limit
 */
export function exceedsDailyLimit(hours: number): boolean {
  const settings = getOrganisationSettings();
  return hours > settings.hoursLimits.dailyHoursLimit;
}

/**
 * Check if hours exceed weekly limit
 */
export function exceedsWeeklyLimit(hours: number): boolean {
  const settings = getOrganisationSettings();
  return hours > settings.hoursLimits.weeklyHoursLimit;
}

/**
 * Get target utilisation rate
 */
export function getTargetUtilisationRate(): number {
  const settings = getOrganisationSettings();
  return settings.utilisation.targetUtilisationRate;
}
